import React, { useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';
import { 
  discover_errors_fetchProjects,
  discover_logs_refetchingTab,
  discover_actions_liked
} from '@paraglide/messages';
import { useNavigate } from '../../../../src/hooks/useI18nNavigate';

import { DiscoverTemplate } from '@edusense/project-ui';
import { Project } from '@edusense/project-types';
import { DiscoverTab } from '@edusense/navigation-types';
import { useAuth } from '@edusense/shared-auth-core';
import {
  GetProjectsType,
  useGetProjectsInfinite,
  useLikeProject,
  useUnlikeProject,
  ProjectResponse as ApiProjectResponse,
} from '@edusense/edusense-project-client';
import { transformProject } from '@edusense/entities-project';

/**
 * DiscoverPage component - Project discovery page (API integration)
 */
const DiscoverPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  
  // 🚨 리렌더링 확인 로깅
  console.log(`🔥🔥🔥 DiscoverPage RENDER: ${location.pathname} at ${Date.now()}`);
  console.log(`🔥 DiscoverPage: Component re-rendered successfully!`);

  // Tab state
  const [activeTab, setActiveTab] = useState<DiscoverTab>('recommended');
  const PAGE_SIZE = 12; // Projects per page (adjustable)

  // API 타입 매핑 (memoized)
  const apiProjectType: GetProjectsType = useMemo(() => {
    switch (activeTab) {
      case 'recommended':
        return GetProjectsType.FOR_YOU;
      case 'following':
        return GetProjectsType.FOLLOW;
      default:
        return GetProjectsType.FOR_YOU;
    }
  }, [activeTab]);

  // 🚀 자동 생성된 무한 쿼리 훅 사용 - 모든 상태 관리 자동화!
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetProjectsInfinite(
    {
      page: 0, // 무한 쿼리에서는 자동 관리
      size: PAGE_SIZE,
      type: apiProjectType,
    },
    {
      query: {
        enabled: activeTab !== 'following' || isAuthenticated, // following 탭은 인증 시에만
        getNextPageParam: (lastPage) => {
          const pageInfo = lastPage.data?.page;
          return pageInfo?.last ? undefined : (pageInfo?.number ?? 0) + 1;
        },
        staleTime: 1000 * 60 * 5, // 5분
      }
    }
  );

  // Mutation 훅들
  const likeProjectMutation = useLikeProject();
  const unlikeProjectMutation = useUnlikeProject();

  // 🎯 모든 프로젝트 데이터를 자동으로 평면화
  const projects: Project[] = useMemo(() => {
    if (!data?.pages) return [];
    
    return data.pages
      .flatMap(page => page.data?.page?.content ?? [])
      .map(transformProject);
  }, [data]);

  // 🚀 에러 메시지 처리 (자동화)
  const errorMessage = useMemo(() => {
    if (!error) return null;
    return error instanceof Error ? error.message : discover_errors_fetchProjects();
  }, [error]);

  // 🚀 간단해진 탭 클릭 핸들러 - React Query가 모든 상태 관리!
  const handleTabClick = useCallback((tabId: DiscoverTab) => {
    if (tabId === activeTab) {
      console.log(discover_logs_refetchingTab({ tab: tabId }));
      // React Query의 refetch로 간단히 해결!
      window.location.reload(); // 임시: 나중에 refetch() 사용
    } else {
      setActiveTab(tabId); // 탭만 변경하면 자동으로 새 쿼리 실행!
    }
  }, [activeTab]);

  // Project detail view handler (modified)
  const handleViewDetails = useCallback((id: string) => {
    // React Router v7 호환: 같은 레벨 경로로 이동
    navigate(`project/${id}`);
  }, [navigate]);

  // 🚀 자동화된 좋아요 핸들링 - mutation 훅 사용!
  const handleLike = useCallback(async (id: string) => {
    try {
      console.log(discover_actions_liked({ id }));
      await likeProjectMutation.mutateAsync({ id });
      // React Query가 자동으로 UI 업데이트!
    } catch (error) {
      console.error('좋아요 처리 중 오류:', error);
    }
  }, [likeProjectMutation]);

  // Comment handling
  const handleComment = useCallback((id: string) => {
    // React Router v7 호환: 프로젝트 상세 페이지로 이동
    navigate(`project/${id}`);
  }, [navigate]);

  // 🚀 자동화된 무한 스크롤 - 한 줄로 해결!
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage && (activeTab !== 'following' || isAuthenticated)) {
      fetchNextPage(); // React Query의 자동 무한 스크롤!
    }
  }, [hasNextPage, isFetchingNextPage, activeTab, isAuthenticated, fetchNextPage]);

  return (
    <DiscoverTemplate
      projects={projects}
      isLoading={isLoading}
      error={errorMessage}
      activeTab={activeTab}
      onTabChange={handleTabClick}
      onViewDetails={handleViewDetails}
      onLike={handleLike}
      onComment={handleComment}
      onLoadMore={handleLoadMore}
      hasMoreProjects={hasNextPage || false}
    />
  );
};

export default DiscoverPage; 